import { GetterTree } from "vuex";
import { ProjectTheme, StoreState, V2Project } from "./adminTypes";
import { RootState } from "../types";
import { emptyResource } from "../emptyModels";
import config from "@/lib/config";

export enum Getters {
  GET_CURRENT_PROJECT_THEME = "GET_CURRENT_THEME",
  GET_LITERALS = "GET_LITERALS",
  GET_CURRENT_PROJECT = "GET_CURRENT_PROJECT",
  GET_PROJECTS = "GET_PROJECTS",
  GET_CURRENT_PROJECT_BENCHMARKS = "GET_CURRENT_PROJECT_BENCHMARKS",
  GET_CURRENT_PROJECT_AGGREGATES = "GET_CURRENT_PROJECT_AGGREGATES",
  GET_CURRENT_PROJECT_SHORT_ASSESSMENTS = "GET_CURRENT_PROJECT_SHORT_ASSESSMENTS",
  GET_CURRENT_PROJECT_SHORT_REPORTS = "GET_CURRENT_PROJECT_SHORT_REPORTS",
  GET_USER = "GET_USER",
  CURRENT_PROJECT_DIVERSITY_ENABLED = "CURRENT_PROJECT_DIVERSITY_ENABLED",
  CURRENT_PROJECT_FOUR_GROUPS_ENABLED = "CURRENT_PROJECT_FOUR_GROUPS_ENABLED",
  GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID = "GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID",
}

export const getters: GetterTree<StoreState, RootState> = {
  [Getters.GET_CURRENT_PROJECT_THEME](state, getters) {
    if (state.currentProject) {
      // All projects
      if (state.currentProject.identifier === "") {
        const rootBenchId = getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID;
        if (rootBenchId === config.CPA_BENCHMARK_ID) {
          return ProjectTheme.CPA_DEFAULT;
        }
        return state.currentProject.theme || ProjectTheme.DEFAULT_TA;
      }
      return state.currentProject.theme || ProjectTheme.DEFAULT_TA;
    }
    return ProjectTheme.DEFAULT_TA;
  },
  [Getters.GET_CURRENT_PROJECT](state) {
    return state.currentProject;
  },
  [Getters.GET_PROJECTS](state): V2Project[] {
    return state.organizationProjects;
  },
  [Getters.GET_CURRENT_PROJECT_BENCHMARKS](state) {
    if (!state.currentProject) {
      return [];
    }

    if ((state.currentProject as any).projectId) {
      // TODO: There is a mixmatch between old api calls and new, this needs to be fixed.
      return state.projectBenchmarks[(state.currentProject as any).projectId] ?? [];
    } else {
      return state.projectBenchmarks[state.currentProject.identifier] ?? [];
    }
  },
  [Getters.GET_LITERALS](state) {
    return (module: string, version: string) => {
      if (!state.literals[module] || !state.literals[module][version]) {
        return emptyResource();
      }

      return state.literals[module][version];
    };
  },
  [Getters.CURRENT_PROJECT_DIVERSITY_ENABLED](state) {
    return state.currentProject?.features?.includes("diversity") || false;
  },
  [Getters.CURRENT_PROJECT_FOUR_GROUPS_ENABLED](state) {
    return state.currentProject?.features?.includes("four_competency_groups") || false;
  },
  [Getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID](state) {
    if (state.currentProject?.identifier !== "") {
      return state.currentProject?.rootBenchmarkId || "";
    } else if (state.organizationProjects.length > 0) {
      return state.organizationProjects[0].rootBenchmarkId || "";
    }
    return "";
  },
  [Getters.GET_CURRENT_PROJECT_AGGREGATES](state) {
    return state.projectAggregates[state.currentProject?.identifier || ""] || {};
  },
  [Getters.GET_CURRENT_PROJECT_SHORT_ASSESSMENTS](state) {
    const currentProject = state.currentProject;
    if (!currentProject || currentProject.identifier === "") {
      const assessments = state.organizationProjects.flatMap((p) => p.assessments || []);
      return assessments.filter((obj, index) => assessments.findIndex((item) => item.identifier === obj.identifier) === index);
    }
    return state.organizationProjects.find((p) => p.identifier === currentProject.identifier)?.assessments || [];
  },
  [Getters.GET_CURRENT_PROJECT_SHORT_REPORTS](state) {
    const currentProject = state.currentProject;
    if (!currentProject || currentProject.identifier === "") {
      const reports = state.organizationProjects.flatMap((p) => p.reports || []);
      return reports.filter((obj, index) => reports.findIndex((item) => item.identifier === obj.identifier) === index).sort((a, b) => b.dateCreated.localeCompare(a.dateCreated));
    }
    return (state.organizationProjects.find((p) => p.identifier === currentProject.identifier)?.reports || []).sort((a, b) => b.dateCreated.localeCompare(a.dateCreated));
  },
};
